import { Mail, MapPin } from "lucide-react";
import { PageHero, SectionHead, Reveal } from "@/components/bits/Reveal";
import ContactForm from "@/components/forms/ContactForm";
import { CONTACT, IMAGES } from "@/data/content";
import { useSEO } from "@/lib/seo";

const WaIcon = ({ size = 22 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

export default function Contact() {
  useSEO(
    "Contact Us | Maqline Solutions Pvt Ltd, Bengaluru",
    "Contact Maqline Solutions Pvt Ltd for manpower requirements, job opportunities, internships or college partnerships. Bhoganahalli, Bengaluru – 560103."
  );

  const cards = [
    { icon: Mail, title: "Students, Candidates & Careers", value: "career@maqlinesolutions.com", href: "mailto:career@maqlinesolutions.com", testId: "contact-email-career", uses: "Jobs · Internships · Industrial training · Candidate support · College/TPO partnerships" },
    { icon: Mail, title: "Businesses & Employers", value: "info@maqlinesolutions.com", href: "mailto:info@maqlinesolutions.com", testId: "contact-email-info", uses: "Manpower · Contract staffing · Recruitment · Workforce solutions · Corporate enquiries" },
    { icon: WaIcon, title: "WhatsApp Us", value: "Chat with our team", href: `https://wa.me/${CONTACT.phoneRaw}?text=${encodeURIComponent("Hello Maqline Solutions, I would like to know more about your services.")}`, testId: "contact-whatsapp" },
  ];

  return (
    <main data-testid="contact-page">
      <PageHero
        overline="Contact"
        titleLines={["Let's start the", "conversation."]}
        description="Manpower requirements, job opportunities, internships or college partnerships — one team handles them all."
        image={IMAGES.automotive}
      />

      <section className="bg-steel py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-16">
            {cards.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.08}>
                <a
                  href={c.href}
                  target={c.href.startsWith("http") ? "_blank" : undefined}
                  rel="noreferrer"
                  data-testid={c.testId}
                  className="group block bg-white border border-ink/10 hover:border-gold/50 rounded-sm p-7 transition-all duration-400 hover:-translate-y-1 hover:shadow-[0_16px_40px_rgba(6,27,58,0.1)]"
                >
                  <div className="w-12 h-12 bg-navy/5 border border-gold/30 rounded-sm flex items-center justify-center mb-5 text-gold group-hover:bg-gold/10 transition-colors duration-300">
                    <c.icon size={22} />
                  </div>
                  <h3 className="font-display text-lg text-ink">{c.title}</h3>
                  <p className="text-sm text-navy font-semibold mt-1.5 break-words">{c.value}</p>
                  {c.uses && <p className="text-[11px] text-muted mt-2 leading-relaxed">{c.uses}</p>}
                </a>
              </Reveal>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14">
            <div>
              <SectionHead overline="Visit Us" title="Maqline Solutions Pvt Ltd" />
              <Reveal delay={0.15}>
                <div className="mt-8 bg-darknavy noise-bg rounded-sm p-8 text-white" data-testid="contact-address-card">
                  <MapPin size={22} className="text-lightgold mb-4" />
                  <p className="text-white/85 leading-relaxed text-sm">
                    Site No 8, MK Layout,<br />
                    Embassy Tech Village Back Gate,<br />
                    Bhoganahalli,<br />
                    Bengaluru – 560103,<br />
                    Karnataka, India
                  </p>
                  <div className="h-px bg-white/10 my-6" />
                  <div className="space-y-2 text-sm text-white/70">
                    <p><span className="text-lightgold font-semibold">Students & Careers:</span> <a href="mailto:career@maqlinesolutions.com" className="hover:text-lightgold transition-colors">career@maqlinesolutions.com</a></p>
                    <p><span className="text-lightgold font-semibold">Businesses & Employers:</span> <a href="mailto:info@maqlinesolutions.com" className="hover:text-lightgold transition-colors">info@maqlinesolutions.com</a></p>
                    <p><span className="text-lightgold font-semibold">Website:</span> {CONTACT.website}</p>
                  </div>
                </div>
              </Reveal>
            </div>
            <div>
              <SectionHead overline="Write To Us" title="Send us a message" />
              <Reveal delay={0.2}>
                <div className="mt-8 border border-ink/10 rounded-sm p-6 sm:p-8 bg-white">
                  <ContactForm />
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
